/**
 * Calendrier de livraison / retrait
 * - Calques calendaires distincts pour la livraison et le retrait
 * - Jours non disponibles rendus non cliquables
 * - Créneaux de 2h avec règles par mode
 */
(function () {
  const MAX_FORWARD_DAYS = 30;
  const CALENDAR_PAGE_SIZE = 14;
  const MODE_CONFIG = {
    delivery: {
      label: 'Livraison',
      allowedDays: [2, 3, 4, 5], // mardi à vendredi
      timeWindows: {
        default: { start: 10, end: 19 }
      },
      dateFallbackLabel: 'Aucune date de livraison disponible.'
    },
    pickup: {
      label: 'Retrait en boutique',
      allowedDays: [2, 3, 4, 5, 6], // mardi à samedi
      timeWindows: {
        2: { start: 11, end: 19 }, // mardi
        default: { start: 10, end: 19 }
      },
      dateFallbackLabel: 'Aucune date de retrait disponible.'
    }
  };

  const DAY_LABEL_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric'
  });

  const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric'
  });

  const FULL_DATE_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  document.addEventListener('DOMContentLoaded', () => initSchedulers(document));
  document.addEventListener('shopify:section:load', (event) => initSchedulers(event.target));
  document.addEventListener('shopify:block:select', (event) => initSchedulers(event.target));

  function initSchedulers(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const containers = root.querySelectorAll('.delivery-scheduler');
    containers.forEach((container) => {
      if (container.dataset.schedulerInit === 'true') return;
      new DeliveryScheduler(container);
      container.dataset.schedulerInit = 'true';
    });
  }

  class DeliveryScheduler {
    constructor(container) {
      this.container = container;
      this.idPrefix = container.dataset.idPrefix || '';
      this.productFormId = container.dataset.productFormId || '';
      this.defaultMode = container.dataset.defaultMode || 'delivery';
      this.modes = {
        delivery: container.dataset.modeDelivery === 'true',
        pickup: container.dataset.modePickup === 'true'
      };
      this.availableModes = Object.keys(this.modes).filter((mode) => this.modes[mode] && MODE_CONFIG[mode]);
      this.modeInputs = Array.from(
        container.querySelectorAll(`input[name="delivery-mode-${this.idPrefix}"]`)
      );
      this.modeElements = this.collectModeElements();
      this.errorEl = container.querySelector('[data-role="scheduler-error"]');
      this.hiddenInputs = {
        mode: container.querySelector('input[data-scheduler-input="mode"]'),
        date: container.querySelector('input[data-scheduler-input="date"]'),
        slot: container.querySelector('input[data-scheduler-input="slot"]')
      };
      this.selectedSlotButtons = {};
      this.modeState = {};
      this.currentMode = null;
      this.today = new Date();
      this.today.setHours(0, 0, 0, 0);

      this.productForm = this.resolveProductForm();
      this.setup();
    }

    collectModeElements() {
      const map = {};
      const wrappers = this.container.querySelectorAll('.delivery-scheduler__mode-content');
      wrappers.forEach((wrapper) => {
        const mode = wrapper.dataset.mode;
        if (!mode) return;
        map[mode] = {
          wrapper,
          calendarGrid: wrapper.querySelector('[data-role="calendar-grid"]'),
          calendarMonth: wrapper.querySelector('[data-role="calendar-month"]'),
          calendarNavPrev: wrapper.querySelector('[data-role="calendar-nav"][data-direction="prev"]'),
          calendarNavNext: wrapper.querySelector('[data-role="calendar-nav"][data-direction="next"]'),
          dateDisplay: wrapper.querySelector('[data-role="scheduler-date-display"]'),
          slotsWrapper: wrapper.querySelector('[data-role="scheduler-slots"]'),
          slotHint: wrapper.querySelector('[data-role="scheduler-slots-hint"]')
        };
      });
      return map;
    }

    setup() {
      if (!this.availableModes.length) return;

      this.availableModes.forEach((mode) => {
        this.modeState[mode] = {
          days: this.buildDaysList(mode),
          date: null,
          slot: '',
          calendarOffset: 0
        };
        this.initializeMode(mode);
      });

      this.bindEvents();
      this.setMode(this.defaultMode);
    }

    bindEvents() {
      this.modeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            this.setMode(input.value);
          }
        });
      });

      this.availableModes.forEach((mode) => {
        const elements = this.modeElements[mode];
        if (!elements) return;
        if (elements.calendarNavPrev) {
          elements.calendarNavPrev.addEventListener('click', () => {
            const state = this.modeState[mode];
            if (!state) return;
            if (state.calendarOffset > 0) {
              state.calendarOffset -= 1;
              this.renderCalendar(mode);
            }
          });
        }
        if (elements.calendarNavNext) {
          elements.calendarNavNext.addEventListener('click', () => {
            const state = this.modeState[mode];
            if (!state) return;
            const maxOffset = this.getMaxCalendarOffset(state.days.length);
            if (state.calendarOffset < maxOffset) {
              state.calendarOffset += 1;
              this.renderCalendar(mode);
            }
          });
        }
      });

      if (this.productForm) {
        this.productForm.addEventListener('submit', (event) => {
          const mode = this.getHiddenValue('mode');
          const date = this.getHiddenValue('date');
          const slot = this.getHiddenValue('slot');
          console.log('[DeliveryScheduler] Form submit - values:', { mode, date, slot });
          const hasDate = Boolean(date);
          const hasSlot = Boolean(slot);
          if (!hasDate || !hasSlot) {
            event.preventDefault();
            this.showError('Merci de sélectionner une date et un créneau horaire avant de continuer.');
          } else {
            // Vérifier que les champs cachés sont bien dans le formulaire
            const formData = new FormData(this.productForm);
            console.log('[DeliveryScheduler] FormData properties:', {
              mode: formData.get('properties[Mode de réception]'),
              date: formData.get('properties[Date de réception]'),
              slot: formData.get('properties[Créneau horaire]')
            });
          }
        });
      }
    }

    initializeMode(mode) {
      const state = this.modeState[mode];
      const elements = this.modeElements[mode];
      if (!state || !elements) return;

      const firstAllowed = state.days.find((day) => day.allowed);
      if (firstAllowed) {
        state.date = new Date(firstAllowed.date.getTime());
        state.calendarOffset = 0;
      }

      this.ensureSelectedDateVisible(mode);
      this.renderCalendar(mode);
      this.renderSlots(mode);
    }

    setMode(mode) {
      if (!this.modes[mode]) {
        mode = this.availableModes[0];
      }
      if (!mode) return;

      this.currentMode = mode;
      this.availableModes.forEach((modeKey) => {
        const elements = this.modeElements[modeKey];
        if (!elements) return;
        elements.wrapper.classList.toggle(
          'delivery-scheduler__mode-content--active',
          modeKey === mode
        );
      });

      this.modeInputs.forEach((input) => {
        input.checked = input.value === mode;
      });

      this.ensureSelectedDateVisible(mode);
      this.renderCalendar(mode);
      this.renderSlots(mode);
      this.updateHiddenInputs();
      this.clearError();
    }

    buildDaysList(mode) {
      const days = [];
      const config = MODE_CONFIG[mode];
      if (!config) return days;

      for (let offset = 0; offset <= MAX_FORWARD_DAYS; offset += 1) {
        const date = new Date(this.today);
        date.setDate(this.today.getDate() + offset);
        date.setHours(0, 0, 0, 0);
        days.push({
          date,
          iso: this.formatDateForInput(date),
          allowed: config.allowedDays.includes(date.getDay())
        });
      }
      return days;
    }

    ensureSelectedDateVisible(mode) {
      const state = this.modeState[mode];
      if (!state || !state.date) return;
      const pageSize = this.getCalendarPageSize();
      const index = state.days.findIndex((day) => this.datesEqual(day.date, state.date));
      if (index === -1) return;
      const targetOffset = Math.floor(index / pageSize);
      if (state.calendarOffset !== targetOffset) {
        state.calendarOffset = targetOffset;
      }
    }

    renderCalendar(mode) {
      const state = this.modeState[mode];
      const elements = this.modeElements[mode];
      if (!state || !elements || !elements.calendarGrid) return;

      const pageSize = this.getCalendarPageSize();
      const maxOffset = this.getMaxCalendarOffset(state.days.length);
      if (state.calendarOffset > maxOffset) {
        state.calendarOffset = maxOffset;
      }

      const startIndex = state.calendarOffset * pageSize;
      const pageDays = state.days.slice(startIndex, startIndex + pageSize);

      elements.calendarGrid.innerHTML = '';

      if (!pageDays.length) {
        const empty = document.createElement('p');
        empty.className = 'delivery-scheduler__calendar-empty';
        empty.textContent = MODE_CONFIG[mode]?.dateFallbackLabel || 'Aucune date disponible.';
        elements.calendarGrid.appendChild(empty);
        this.updateCalendarNavState(elements, state, maxOffset);
        this.updateDateDisplay(mode);
        return;
      }

      const fragment = document.createDocumentFragment();
      pageDays.forEach((item) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'delivery-scheduler__calendar-day';
        button.setAttribute('aria-label', FULL_DATE_FORMATTER.format(item.date));
        button.dataset.value = item.iso;
        button.textContent = this.formatCalendarDayLabel(item.date);
        button.disabled = !item.allowed;
        button.setAttribute('role', 'gridcell');
        if (!item.allowed) {
          button.classList.add('delivery-scheduler__calendar-day--disabled');
        }
        if (state.date && this.datesEqual(state.date, item.date)) {
          button.classList.add('delivery-scheduler__calendar-day--active');
        }
        if (item.allowed) {
          button.addEventListener('click', () => {
            this.selectDate(mode, item.date);
          });
        }
        fragment.appendChild(button);
      });

      elements.calendarGrid.appendChild(fragment);
      this.updateCalendarNavState(elements, state, maxOffset);
      this.updateCalendarMonthLabel(elements, pageDays);
      this.updateDateDisplay(mode);
    }

    selectDate(mode, dateObj) {
      const state = this.modeState[mode];
      if (!state) return;

      state.date = new Date(dateObj.getTime());
      state.date.setHours(0, 0, 0, 0);
      state.slot = '';
      this.ensureSelectedDateVisible(mode);
      this.renderCalendar(mode);
      this.renderSlots(mode);
      if (mode === this.currentMode) {
        this.updateHiddenInputs();
        this.clearError();
      }
    }

    renderSlots(mode) {
      const state = this.modeState[mode];
      const elements = this.modeElements[mode];
      if (!state || !elements || !elements.slotsWrapper) return;

      elements.slotsWrapper.innerHTML = '';
      this.selectedSlotButtons[mode] = null;

      if (!state.date) {
        if (elements.slotHint) {
          elements.slotHint.textContent = 'Veuillez choisir une date disponible.';
        }
        if (mode === this.currentMode) {
          this.updateHiddenInputs();
        }
        return;
      }

      const slots = this.buildSlotsForDate(mode, state.date);
      if (!slots.length) {
        if (elements.slotHint) {
          elements.slotHint.textContent = 'Aucun créneau disponible pour cette date.';
        }
        state.slot = '';
        if (mode === this.currentMode) {
          this.updateHiddenInputs();
        }
        return;
      }

      if (elements.slotHint) {
        elements.slotHint.textContent = 'Sélectionnez un créneau horaire.';
      }

      slots.forEach((slot) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'delivery-scheduler__slot';
        button.dataset.value = slot;
        button.textContent = slot;
        button.addEventListener('click', () => {
          this.selectSlot(mode, button, slot);
        });
        if (state.slot === slot) {
          this.selectSlot(mode, button, slot, { updateHidden: mode === this.currentMode });
        }
        elements.slotsWrapper.appendChild(button);
      });

      if (!state.slot && elements.slotsWrapper.firstElementChild) {
        const firstButton = elements.slotsWrapper.firstElementChild;
        this.selectSlot(mode, firstButton, firstButton.dataset.value, {
          updateHidden: mode === this.currentMode
        });
      }
    }

    selectSlot(mode, button, slotValue, options = {}) {
      const { updateHidden = true } = options;
      const state = this.modeState[mode];
      if (!state) return;

      if (this.selectedSlotButtons[mode]) {
        this.selectedSlotButtons[mode].classList.remove('delivery-scheduler__slot--active');
      }
      this.selectedSlotButtons[mode] = button;
      button.classList.add('delivery-scheduler__slot--active');
      state.slot = slotValue;

      if (updateHidden && mode === this.currentMode) {
        this.updateHiddenInputs();
        this.clearError();
      }
    }

    buildSlotsForDate(mode, dateObj) {
      if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
        return [];
      }
      const weekday = dateObj.getDay();
      const config = MODE_CONFIG[mode];
      if (!config || !config.allowedDays.includes(weekday)) {
        return [];
      }

      const windowConfig = this.getWindowForModeAndDay(mode, weekday);
      if (!windowConfig) {
        return [];
      }

      const slotLength = 2;
      const intervals = [];
      for (let start = windowConfig.start; start + slotLength <= windowConfig.end; start += slotLength) {
        intervals.push({ start, end: start + slotLength });
      }
      if (intervals.length) {
        const last = intervals[intervals.length - 1];
        if (last.end < windowConfig.end) {
          last.end = windowConfig.end;
        }
      }
      return intervals.map((interval) => `${this.formatHour(interval.start)} - ${this.formatHour(interval.end)}`);
    }

    getWindowForModeAndDay(mode, weekday) {
      const config = MODE_CONFIG[mode];
      if (!config) return null;
      const specificWindow = config.timeWindows[weekday];
      const windowConfig = specificWindow || config.timeWindows.default;
      if (!windowConfig) return null;
      return {
        start: Number(windowConfig.start),
        end: Number(windowConfig.end)
      };
    }

    updateCalendarNavState(elements, state, maxOffset) {
      if (elements.calendarNavPrev) {
        elements.calendarNavPrev.disabled = state.calendarOffset <= 0;
      }
      if (elements.calendarNavNext) {
        elements.calendarNavNext.disabled = state.calendarOffset >= maxOffset;
      }
    }

    updateCalendarMonthLabel(elements, pageDays) {
      if (!elements.calendarMonth) return;
      if (!pageDays.length) {
        elements.calendarMonth.textContent = '';
        return;
      }
      const first = pageDays[0].date;
      const last = pageDays[pageDays.length - 1].date;
      const firstLabel = capitalizeFirst(MONTH_LABEL_FORMATTER.format(first));
      if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
        elements.calendarMonth.textContent = firstLabel;
        return;
      }
      const secondLabel = capitalizeFirst(MONTH_LABEL_FORMATTER.format(last));
      if (first.getFullYear() === last.getFullYear()) {
        elements.calendarMonth.textContent = `${firstLabel} – ${secondLabel}`;
      } else {
        elements.calendarMonth.textContent = `${firstLabel} / ${secondLabel}`;
      }
    }

    updateDateDisplay(mode) {
      const elements = this.modeElements[mode];
      const state = this.modeState[mode];
      if (!elements || !elements.dateDisplay) return;

      if (state?.date) {
        elements.dateDisplay.textContent = `Date choisie : ${capitalizeFirst(this.formatDateForDisplay(state.date))}`;
      } else {
        elements.dateDisplay.textContent = 'Sélectionnez une date disponible.';
      }
    }

    updateHiddenInputs() {
      if (!this.currentMode) return;
      const modeConfig = MODE_CONFIG[this.currentMode] || {};
      const state = this.modeState[this.currentMode] || {};
      this.setHiddenValue('mode', modeConfig.label || this.currentMode);
      this.setHiddenValue('date', state.date ? this.formatDateForDisplay(state.date) : '');
      this.setHiddenValue('slot', state.slot || '');
    }

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
      }
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.textContent = '';
      }
    }

    resolveProductForm() {
      let form = null;
      if (this.productFormId) {
        form = document.getElementById(this.productFormId);
      }
      if (!form) {
        form = this.container.closest('.product__info-container')?.querySelector('form[action*="/cart/add"]');
      }
      if (!form) {
        form = document.querySelector('form[action*="/cart/add"]');
      }
      if (form instanceof HTMLFormElement) {
        if (!form.id) {
          form.id = `product-form-${Date.now()}`;
        }
        this.attachHiddenInputsToForm(form);
        return form;
      }
      return null;
    }

    attachHiddenInputsToForm(form) {
      if (!form || !form.id) return;
      Object.values(this.hiddenInputs).forEach((input) => {
        if (!input) return;
        input.setAttribute('form', form.id);
        // S'assurer que les champs sont bien attachés même s'ils ont déjà l'attribut form
        if (!input.form || input.form.id !== form.id) {
          input.setAttribute('form', form.id);
        }
      });
    }

    getCalendarPageSize() {
      return CALENDAR_PAGE_SIZE;
    }

    getMaxCalendarOffset(totalDays) {
      if (totalDays <= 0) return 0;
      return Math.max(0, Math.ceil(totalDays / this.getCalendarPageSize()) - 1);
    }

    formatCalendarDayLabel(date) {
      const label = DAY_LABEL_FORMATTER.format(date).replace('.', '');
      return capitalizeFirst(label);
    }

    formatDateForInput(date) {
      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')
      ].join('-');
    }

    formatDateForDisplay(date) {
      return FULL_DATE_FORMATTER.format(date);
    }

    formatHour(hour) {
      return `${String(hour).padStart(2, '0')}h00`;
    }

    datesEqual(a, b) {
      return (
        a instanceof Date &&
        b instanceof Date &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    }

    setHiddenValue(key, value) {
      const input = this.hiddenInputs[key];
      if (input) {
        input.value = value;
        // Debug temporaire - à retirer après vérification
        if (value) {
          console.log(`[DeliveryScheduler] Set ${key} = ${value}`, input);
        }
      } else {
        console.warn(`[DeliveryScheduler] Input not found for key: ${key}`, this.hiddenInputs);
      }
    }

    getHiddenValue(key) {
      const input = this.hiddenInputs[key];
      return input ? input.value : '';
    }
  }

  function capitalizeFirst(value) {
    if (typeof value !== 'string' || !value.length) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
})();

